import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Page, Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { VoucherStatus } from 'constants/vouchers';
import { useManagedQuery } from 'utils/managedHooks';
import { GET_USER } from 'store/queries';
import VoucherTransactionsTable from './VoucherTransactionsTable';
import VoucherDetails from './VoucherDetails';
import VoucherNotes from './VoucherNotes';
import AddVoucherNoteForm from './AddVoucherNoteForm';
import { StyledH2 } from './VoucherDetails.styles';
import GET_VOUCHER_DETAILS from './queries/GET_VOUCHER_DETAILS';
import GET_VOUCHER_NOTES from './queries/GET_VOUCHER_NOTES';

const VoucherDetailsWrapper = ({
  match: {
    params: { voucherAlias }
  }
}) => {
  const {
    data: {
      me: { canEnableVouchers, canDisableVouchers, canUpdateVouchers, canManageVouchers, canRetrieveVouchers }
    }
  } = useManagedQuery(GET_USER);

  const {
    loading: voucherDetailsLoading,
    data: voucherDetailsData,
    error: voucherDetailsError
  } = useQuery(GET_VOUCHER_DETAILS, {
    variables: {
      voucherAlias
    },
    fetchPolicy: 'network-only'
  });

  const [getVoucherNotesQuery, { data: voucherNotesData, error: voucherNotesError, refetch: refetchVoucherNotes }] =
    useLazyQuery(GET_VOUCHER_NOTES);

  const {
    findVoucherByReferenceAndMarket: {
      alias,
      type,
      currency,
      value,
      balance,
      status,
      customerEmail,
      firstName,
      lastName,
      salesPeriodStart,
      salesPeriodEnd,
      travelPeriodStart,
      travelPeriodEnd,
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
      activatedAt,
      inactivatedAt,
      payments = [],
      description,
      language,
      associatedPNR,
      associatedVoucher,
      replacedByVoucher,
      originalSalesPeriodEnd,
      originalTravelPeriodEnd
    } = {}
  } = voucherDetailsData || {};

  useEffect(() => {
    if (alias) {
      getVoucherNotesQuery({
        variables: {
          voucherAlias: alias
        }
      });
    }
  }, [alias, getVoucherNotesQuery]);

  if (!canManageVouchers) {
    return <Redirect to="/" />;
  }

  if (!canRetrieveVouchers) {
    return <Redirect to="/" />;
  }

  const { getVoucherNotes: voucherNotes } = voucherNotesData || {};

  const loading = voucherDetailsLoading;
  const error = voucherDetailsError;
  if (loading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/vouchers/manage'
    });
  }

  const canDisableVoucher = VoucherStatus[status] === VoucherStatus.ACTIVE && canDisableVouchers;
  const canEnableVoucher = VoucherStatus[status] === VoucherStatus.INACTIVE && canEnableVouchers && !replacedByVoucher;
  const isExtendable = !originalSalesPeriodEnd && !originalTravelPeriodEnd;

  return (
    <Page columns={20}>
      <Row span={18} start={2}>
        <h1>
          <FormattedMessage id="vouchers.manageVoucher" />
        </h1>
        <VoucherDetails
          firstName={firstName}
          lastName={lastName}
          email={customerEmail}
          voucherAlias={alias}
          voucherCurrency={currency}
          voucherOriginalValue={value}
          voucherCurrentBalance={balance}
          voucherDescription={description}
          voucherType={type}
          voucherStatus={status}
          salesPeriodStart={salesPeriodStart}
          salesPeriodEnd={salesPeriodEnd}
          travelPeriodStart={travelPeriodStart}
          travelPeriodEnd={travelPeriodEnd}
          createdBy={createdBy}
          createdAt={createdAt}
          updatedBy={updatedBy}
          updatedAt={updatedAt}
          activatedAt={activatedAt}
          inactivatedAt={inactivatedAt}
          language={language}
          associatedPNR={associatedPNR}
          canDisableVoucher={canDisableVoucher}
          canEnableVoucher={canEnableVoucher}
          canUpdateVoucher={canUpdateVouchers && isExtendable}
          associatedVoucher={associatedVoucher}
          replacedByVoucher={replacedByVoucher}
          originalSalesPeriodEnd={originalSalesPeriodEnd}
          originalTravelPeriodEnd={originalTravelPeriodEnd}
        />
        <StyledH2>
          <FormattedMessage id="notes.notes" />
        </StyledH2>
        <AddVoucherNoteForm voucherAlias={alias} refetchVoucherNotes={refetchVoucherNotes} />
        <VoucherNotes
          notes={voucherNotes?.map((note) => ({
            id: note.id,
            createdAt: note.createdAt,
            author: note.createdBy,
            content: note.content
          }))}
          hasError={Boolean(voucherNotesError)}
        />
        <StyledH2>
          <FormattedMessage id="vouchers.transactions" />
        </StyledH2>
        <VoucherTransactionsTable
          transactions={payments.map((payment) => ({
            amount: payment.amount,
            currency: payment.currency,
            status: payment.status,
            pnr: payment.bookingReference
          }))}
        />
      </Row>
    </Page>
  );
};

VoucherDetailsWrapper.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      voucherAlias: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default withRouter(VoucherDetailsWrapper);
